import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Button,
  Card,
  Heading,
  Table,
  TextField,
  Dialog,
  AlertDialog,
  Badge,
  Checkbox
} from '@radix-ui/themes';
import { PlusIcon, ReloadIcon, PersonIcon, TrashIcon } from '@radix-ui/react-icons';
import { toast } from 'react-hot-toast';
import ClinicLayout from './ClinicLayout';
import { clinicApi } from '../../services/api';
import { useClinicAdmin } from '../../hooks/useClinicAdmin';
import { useOrganization } from '../../contexts/OrganizationContext';

interface ClinicUser {
  id: string;
  email: string;
  name?: string | null;
  roleIds: string[];
  addedAt?: string | null;
}

const roleLabels: Record<string, string> = {
  clinical_care_manager: 'Clinical Care Manager',
  billing_operations_manager: 'Billing & Operation Manager',
  clinical_medical_director: 'Clinical Medical Director',
  // PHA-1240: designs Custom Reports / Views in the user's clinics.
  report_designer: 'Report designer',
};

const ASSIGNABLE_ROLE_IDS = [
  'clinical_care_manager',
  'billing_operations_manager',
  'clinical_medical_director',
  'report_designer',
];

const normalizeUsers = (data: any): ClinicUser[] => {
  const records = data?.users || data?.records || data || [];
  if (!Array.isArray(records)) return [];

  return records.map((record: any) => {
    const rawRoleIds: string[] = Array.isArray(record.role_ids)
      ? record.role_ids
      : record.role_id
        ? [record.role_id]
        : record.role
          ? [record.role]
          : [];
    return {
      id: String(record.id ?? record.user_id ?? record.userId ?? record.sub),
      email: record.email ?? record.user_email ?? 'unknown',
      name: record.name ?? record.full_name ?? null,
      roleIds: rawRoleIds.filter((r) => ASSIGNABLE_ROLE_IDS.includes(r)),
      addedAt: record.added_at ?? record.addedAt ?? null,
    };
  });
};

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const ClinicUsersPage: React.FC = () => {
  const { orgId: selectedOrgId } = useOrganization();
  const { clinicData } = useClinicAdmin();
  const clinicDomain = clinicData?.domain ? String(clinicData.domain).trim().toLowerCase() : null;

  const [users, setUsers] = useState<ClinicUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteSaving, setInviteSaving] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRoleIds, setInviteRoleIds] = useState<string[]>([]);
  const [inviteUseSSO, setInviteUseSSO] = useState(false);
  const [inviteLinkOpen, setInviteLinkOpen] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [inviteLinkEmail, setInviteLinkEmail] = useState('');
  const [inviteLinkIsSSO, setInviteLinkIsSSO] = useState(false);
  const [inviteLinkLoading, setInviteLinkLoading] = useState(false);
  const [domainError, setDomainError] = useState<string | null>(null);

  const [editRoleUser, setEditRoleUser] = useState<ClinicUser | null>(null);
  const [editRoleSelection, setEditRoleSelection] = useState<string[]>([]);
  const [roleSaving, setRoleSaving] = useState(false);

  const [removeTarget, setRemoveTarget] = useState<ClinicUser | null>(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await clinicApi.getUsers();
      setUsers(normalizeUsers(data));
    } catch (err: any) {
      console.error('Failed to load clinic users:', err);
      setError(err.message || 'Failed to load clinic users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
    // selectedOrgId is in deps so the users list refetches when the user picks
    // a different clinic in the OrganizationSelector (PHA-536).
  }, [loadUsers, selectedOrgId]);

  const roleOptions = useMemo(() => (
    ASSIGNABLE_ROLE_IDS.map((id) => ({ id, label: roleLabels[id] }))
  ), []);

  const toggleInviteRole = (roleId: string) => {
    setInviteRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]
    );
  };

  const toggleEditRole = (roleId: string) => {
    setEditRoleSelection((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]
    );
  };

  const validateDomain = (email: string) => {
    if (!clinicDomain) {
      setDomainError(null);
      return true;
    }

    const emailDomain = email.split('@')[1]?.toLowerCase();
    if (!emailDomain || emailDomain !== clinicDomain) {
      setDomainError(`Email domain must match ${clinicDomain}`);
      return false;
    }

    setDomainError(null);
    return true;
  };

  const openInvite = () => {
    setInviteEmail('');
    setInviteRoleIds([]);
    setInviteUseSSO(false);
    setDomainError(null);
    setInviteOpen(true);
  };

  const openEditRole = (user: ClinicUser) => {
    setEditRoleUser(user);
    setEditRoleSelection(user.roleIds.filter((id) => ASSIGNABLE_ROLE_IDS.includes(id)));
  };

  const copyInviteLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Invite link copied');
    } catch (err: any) {
      console.error('Failed to copy invite link:', err);
      toast.error('Failed to copy invite link');
    }
  };

  const handleResendInvite = async (user: ClinicUser) => {
    setInviteLinkLoading(true);
    try {
      const result = await clinicApi.createInviteLink(user.id);
      const link = result?.invite_link || result?.login_url;
      const isSSO = !!result?.sso || !!result?.login_url;
      if (link) {
        setInviteLinkEmail(user.email);
        setInviteLink(link);
        setInviteLinkIsSSO(isSSO);
        setInviteLinkOpen(true);
        await copyInviteLink(link);
      } else {
        toast.error('Invite link not available');
      }
    } catch (err: any) {
      console.error('Failed to create invite link:', err);
      toast.error(err.message || 'Failed to create invite link');
    } finally {
      setInviteLinkLoading(false);
    }
  };

  const handleInvite = async () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email) {
      toast.error('Email is required');
      return;
    }
    if (inviteRoleIds.length === 0) {
      toast.error('Select at least one role to assign');
      return;
    }
    if (!validateDomain(email)) {
      toast.error('Email domain does not match clinic domain');
      return;
    }

    setInviteSaving(true);
    try {
      const result = await clinicApi.inviteUser(email, inviteRoleIds, inviteUseSSO);
      const link = result?.invite_link || result?.login_url;
      if (link) {
        setInviteLinkEmail(email);
        setInviteLink(link);
        setInviteLinkIsSSO(inviteUseSSO);
        setInviteLinkOpen(true);
        await copyInviteLink(link);
      } else {
        toast.success('User added');
      }
      setInviteOpen(false);
      loadUsers();
    } catch (err: any) {
      console.error('Failed to invite user:', err);
      toast.error(err.message || 'Failed to invite user');
    } finally {
      setInviteSaving(false);
    }
  };

  const handleSaveRole = async () => {
    if (!editRoleUser) return;
    if (editRoleSelection.length === 0) {
      toast.error('Select at least one role');
      return;
    }

    setRoleSaving(true);
    try {
      await clinicApi.updateUserRoles(editRoleUser.id, editRoleSelection);
      toast.success('Roles updated');
      setEditRoleUser(null);
      loadUsers();
    } catch (err: any) {
      console.error('Failed to update roles:', err);
      toast.error(err.message || 'Failed to update roles');
    } finally {
      setRoleSaving(false);
    }
  };

  const handleRemoveUser = async () => {
    if (!removeTarget) return;
    try {
      await clinicApi.removeUser(removeTarget.id);
      toast.success('User removed');
      setRemoveTarget(null);
      loadUsers();
    } catch (err: any) {
      console.error('Failed to remove user:', err);
      toast.error(err.message || 'Failed to remove user');
    }
  };

  const roleDisplay = (user: ClinicUser) => {
    if (!user.roleIds.length) return '—';
    return user.roleIds.map((id) => roleLabels[id] || id).join(', ');
  };

  return (
    <ClinicLayout
      title="Clinic Users"
      subtitle="Invite and manage users in your clinic."
    >
      <Card className="mb-4">
        <Box p="4">
          <Flex align="center" justify="between" gap="3" wrap="wrap">
            <Text size="2" color="gray">
              Users listed below are scoped to your clinic organization.
            </Text>
            <Flex align="center" gap="2">
              <Button variant="soft" onClick={loadUsers} disabled={loading}>
                <ReloadIcon className={loading ? 'animate-spin' : ''} />
                Refresh
              </Button>
              <Button variant="solid" onClick={openInvite}>
                <PlusIcon />
                Invite User
              </Button>
            </Flex>
          </Flex>
        </Box>
      </Card>

      <Card>
        <Box p="4">
          <Heading size="4" className="mb-3">Users</Heading>
          {error && (
            <Text color="red" size="2" className="mb-3 block">{error}</Text>
          )}
          {loading && users.length === 0 ? (
            <Flex justify="center" align="center" style={{ minHeight: 120 }}>
              <ReloadIcon className="animate-spin" width="24" height="24" />
            </Flex>
          ) : (
            <Table.Root variant="surface">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeaderCell>Email</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Role</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Added</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Actions</Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {users.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan={5}>
                      <Text color="gray">No users found.</Text>
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  users.map((user) => (
                    <Table.Row key={user.id}>
                      <Table.Cell>
                        <Text size="2" weight="medium">{user.email}</Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="2">{user.name || '—'}</Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Badge color="gray">{roleDisplay(user)}</Badge>
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="2">{formatDate(user.addedAt)}</Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Flex gap="2" wrap="wrap">
                          <Button variant="soft" onClick={() => openEditRole(user)}>
                            <PersonIcon />
                            Edit Role
                          </Button>
                          <Button
                            variant="soft"
                            onClick={() => handleResendInvite(user)}
                            disabled={inviteLinkLoading}
                          >
                            <PlusIcon />
                            Invite Link
                          </Button>
                          <Button variant="soft" color="red" onClick={() => setRemoveTarget(user)}>
                            <TrashIcon />
                            Remove
                          </Button>
                        </Flex>
                      </Table.Cell>
                    </Table.Row>
                  ))
                )}
              </Table.Body>
            </Table.Root>
          )}
        </Box>
      </Card>

      <Dialog.Root open={inviteOpen} onOpenChange={setInviteOpen}>
        <Dialog.Content style={{ maxWidth: 520 }}>
          <Dialog.Title>Invite Clinic User</Dialog.Title>
          <Dialog.Description size="2" color="gray">
            Enter the user email and assign a role. Invites must match your clinic domain.
          </Dialog.Description>
          <Flex direction="column" gap="3" className="mt-4">
            <Box>
              <TextField.Root>
                <TextField.Input
                  placeholder="Email address"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.currentTarget.value)}
                  onBlur={(e) => validateDomain(e.currentTarget.value)}
                />
              </TextField.Root>
              {domainError && (
                <Text size="1" color="red" className="mt-1 block">{domainError}</Text>
              )}
            </Box>
            <Box>
              <label className="flex items-center gap-2">
                <Checkbox
                  checked={inviteUseSSO}
                  onCheckedChange={(checked) => setInviteUseSSO(checked === true)}
                />
                <Text size="2">Use SSO (Google/Microsoft)</Text>
              </label>
              {inviteUseSSO && (
                <Text size="1" color="gray" className="mt-1 block">
                  User will sign in with their existing Google or Microsoft account. No password setup required.
                </Text>
              )}
            </Box>
            <Box>
              <Text size="2" weight="medium" className="mb-2 block">Assign roles</Text>
              <Flex direction="column" gap="2">
                {roleOptions.map((role) => (
                  <label key={role.id} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={inviteRoleIds.includes(role.id)}
                      onCheckedChange={() => toggleInviteRole(role.id)}
                    />
                    <Text size="2">{role.label}</Text>
                  </label>
                ))}
              </Flex>
            </Box>
            {clinicDomain && (
              <Text size="1" color="gray">
                Allowed domain: {clinicDomain}
              </Text>
            )}
          </Flex>
          <Flex justify="end" gap="2" className="mt-4">
            <Dialog.Close>
              <Button variant="soft" type="button">Cancel</Button>
            </Dialog.Close>
            <Button variant="solid" onClick={handleInvite} disabled={inviteSaving}>
              {inviteSaving ? 'Inviting...' : 'Send Invite'}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      <Dialog.Root open={inviteLinkOpen} onOpenChange={setInviteLinkOpen}>
        <Dialog.Content style={{ maxWidth: 520 }}>
          <Dialog.Title>{inviteLinkIsSSO ? 'SSO User Created' : 'Invite Link'}</Dialog.Title>
          <Dialog.Description size="2" color="gray">
            {inviteLinkIsSSO
              ? `Share this login URL with ${inviteLinkEmail || 'the user'}. They can sign in using their Google or Microsoft account.`
              : `Share this link with ${inviteLinkEmail || 'the user'} to set their password.`}
          </Dialog.Description>
          <Flex direction="column" gap="3" className="mt-4">
            <TextField.Root>
              <TextField.Input value={inviteLink} readOnly />
            </TextField.Root>
            <Text size="2" color="gray">
              {inviteLinkIsSSO
                ? 'The user can log in immediately using SSO.'
                : 'Invite links expire. You can generate a fresh link anytime.'}
            </Text>
          </Flex>
          <Flex justify="end" gap="2" className="mt-4">
            <Button variant="soft" onClick={() => copyInviteLink(inviteLink)} disabled={!inviteLink}>
              Copy Link
            </Button>
            <Button
              variant="solid"
              onClick={() => inviteLink && window.open(inviteLink, '_blank', 'noopener')}
              disabled={!inviteLink}
            >
              Open Link
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      <Dialog.Root open={!!editRoleUser} onOpenChange={(open) => !open && setEditRoleUser(null)}>
        <Dialog.Content style={{ maxWidth: 480 }}>
          <Dialog.Title>Edit Roles</Dialog.Title>
          <Dialog.Description size="2" color="gray">
            Select one or more roles for {editRoleUser?.email}. Users may hold multiple roles
            (e.g. lead physician + biller).
          </Dialog.Description>
          <Box className="mt-4">
            <Flex direction="column" gap="3">
              {roleOptions.map((role) => (
                <label key={role.id} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={editRoleSelection.includes(role.id)}
                    onCheckedChange={() => toggleEditRole(role.id)}
                  />
                  <Text size="2">{role.label}</Text>
                </label>
              ))}
            </Flex>
          </Box>
          <Flex justify="end" gap="2" className="mt-4">
            <Dialog.Close>
              <Button variant="soft" type="button">Cancel</Button>
            </Dialog.Close>
            <Button variant="solid" onClick={handleSaveRole} disabled={roleSaving}>
              {roleSaving ? 'Saving...' : 'Save Roles'}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      <AlertDialog.Root open={!!removeTarget} onOpenChange={(open) => !open && setRemoveTarget(null)}>
        <AlertDialog.Content style={{ maxWidth: 460 }}>
          <AlertDialog.Title>Remove User</AlertDialog.Title>
          <AlertDialog.Description size="2" color="gray">
            Remove {removeTarget?.email} from your clinic? This will revoke access.
          </AlertDialog.Description>
          <Flex justify="end" gap="2" className="mt-4">
            <AlertDialog.Cancel>
              <Button variant="soft">Cancel</Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button variant="solid" color="red" onClick={handleRemoveUser}>
                Remove
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </ClinicLayout>
  );
};

export default ClinicUsersPage;
